import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useContext,
} from 'react';
import {View} from 'react-native';
import {Badge, Divider} from 'react-native-paper';
import {useIsFocused} from '@react-navigation/native';
import {HeaderBackButton} from '@react-navigation/elements';

import sy from '~/styles';
import {Text, TabBar} from '~/components/controls';
import {ReplacementActionsheet} from '~/components/shared/Actionsheets';
import Format from '~/lib/format';
import {Tabs} from '~/types/tabs';
import {addToRecents} from '~/actions';
import {useDispatch} from '~/lib/hooks';
import withServiceRequestShortcuts from '~/components/hoc/with-service-request-shortcuts';

import ChevronLeftIcon from '~/images/md-icons/chevron_left/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import ChatIcon from '~/images/md-icons/chat/materialicons/24px.svg';
import AssignmentOutlinedIcon from '~/images/md-icons/assignment/materialsymbolsoutlined/24px.svg';
import ChatOutlinedIcon from '~/images/md-icons/chat/materialsymbolsoutlined/24px.svg';

import ServiceRequestContext from './context';
import withServiceRequestContext from './components/withServiceRequestContext';
import HeaderRight from './components/HeaderRight';
import MessagesTab from './components/MessagesTab';
import RobDenialsDialog from './components/RobDenialsDialog';
import ReplacementRequestTab from './components/ReplacementRequestTab';
import PropTypes from 'prop-types';

const renderChevronLeftIcon = () => <ChevronLeftIcon />;

const Index = ({navigation, route}) => {
  const initialTab = route.params?.tab ?? Tabs.Request;

  const {
    request_id,
    loading,
    request,
    replacement,
    rob,
    fleetRequest,
    readLastMessage,
    onRefresh,
  } = useContext(ServiceRequestContext);

  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [showEditType, setShowEditType] = useState(false);
  const [showDenialReasonsDialog, setShowDenialReasonsDialog] = useState(false);

  let unreadMessageBadge = null;
  if (readLastMessage === false) {
    unreadMessageBadge = (
      <Badge
        theme={{colors: {notification: '#B3261E'}}}
        visible={true}
        style={{position: 'absolute', top: 0, right: -2}}
        size={8}
      />
    );
  }

  const isFocused = useIsFocused();
  const dispatch = useDispatch();

  const onEditType = () => {
    setShowEditType(true);
  };

  const onShowRobDenials = () => {
    setShowDenialReasonsDialog(true);
  };

  const navigateBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else if (replacement?.request_id === request_id) {
      navigation.replace('Tabs', {
        screen: 'Calendar',
      });
    } else {
      navigation.replace('Request', {
        request_id,
      });
    }
  }, [navigation, replacement, request_id]);

  const renderHeaderLeft = () => (
    <HeaderBackButton
      backImage={renderChevronLeftIcon}
      onPress={navigateBack}
    />
  );

  const renderHeaderTitle = () => (
    <View style={[sy['flex-row']]}>
      <Text style={sy.largePlus}>
        Huurauto&nbsp;&middot;&nbsp;{Format.license(request.license)}
      </Text>
    </View>
  );

  const renderHeaderRight = () => (
    <HeaderRight
      request_id={request?.id}
      replacement={replacement}
      rob={rob}
      fleetRequest={fleetRequest}
      onEditType={onEditType}
      onShowRobDenials={onShowRobDenials}
      onRefresh={onRefresh}
    />
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerLeft: renderHeaderLeft,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerStyle: {
        borderBottomColor: 'transparent',
        boxShadow: 'none',
      },
      headerTitle: renderHeaderTitle,
      headerRight: renderHeaderRight,
    });
  }, [
    navigation,
    navigateBack,
    request,
    replacement,
    rob,
    fleetRequest,
    onRefresh,
  ]);

  useEffect(() => {
    if (isFocused) {
      onRefresh();
      dispatch(addToRecents(replacement?.request_id));
    }
  }, [isFocused, replacement?.request_id, onRefresh]);

  useEffect(() => {
    navigation.setParams({
      tab: selectedTab,
    });
  }, [navigation, selectedTab]);

  return (
    <View style={sy.mainView}>
      {loading ? null : (
        <>
          <TabBar tab={selectedTab} onChangeTab={setSelectedTab}>
            <TabBar.Tab
              label={Tabs.Request}
              icon={{
                filled: <AssignmentIcon />,
                outlined: <AssignmentOutlinedIcon />,
              }}
            />
            <TabBar.Tab
              label={Tabs.Messages}
              badge={unreadMessageBadge}
              icon={{
                filled: <ChatIcon />,
                outlined: <ChatOutlinedIcon />,
              }}
            />
          </TabBar>
          <Divider style={sy['bg-gray88']} />
          <TabBar.Content tab={selectedTab}>
            <ReplacementRequestTab
              tab={Tabs.Request}
              onChangeTab={setSelectedTab}
            />
            <MessagesTab tab={Tabs.Messages} onChangeTab={setSelectedTab} />
          </TabBar.Content>
          {replacement?.request_id != null &&
            replacement?.parent_id != null &&
            replacement.request_id !== replacement.parent_id && (
              <ReplacementActionsheet
                visible={showEditType}
                request_id={request.id}
                license={request.license}
                fleet_contract_id={request.fleet_contract_id}
                parent_request_type={request.request_type}
                type={request.replacement?.type}
                onDismiss={() => setShowEditType(false)}
                onTypeChanged={navigateBack}
              />
            )}
          <RobDenialsDialog
            visible={showDenialReasonsDialog}
            request_id={request.id}
            replacement={replacement}
            rob={rob}
            fleet_request={fleetRequest}
            onRefresh={onRefresh}
            onDismiss={() => setShowDenialReasonsDialog(false)}
          />
        </>
      )}
    </View>
  );
};
Index.propTypes = {
  navigation: PropTypes.any,
  route: PropTypes.any,
};

export default withServiceRequestShortcuts(
  withServiceRequestContext(Index),
  (props) => props.replacement?.request_id,
);
